/* nb-shell-cart-drawer.js
   Renders the cart drawer from /cart.js. Never a literal price, always a live fetch,
   and always formatted through the shop's own money_format (read from a data attribute
   on the drawer root, set by Liquid from `shop.money_format`), so SG ($) and MY (RM)
   both render correctly. Never a price inside a button: the Checkout control carries no
   amount, the live total sits in its own row above it.

   Opens on `cart:refresh` (dispatched by any add-to-cart control after a successful
   /cart/add.js call) and repaints on every quantity change, remove, and offer add.
   Uses the nb-ui.js `drawer` module (data-nb-drawer) for open/close/focus-trap/scrim;
   this file only owns fetching cart state and writing it into the DOM. */
(function () {
  // Every cart instance on the page (the drawer, and/or the /cart page panel) carries
  // data-nb-cart-root. One fetch of /cart.js paints all of them, so the drawer and the
  // page never drift out of sync when both are present.
  // Translatable strings, rendered by snippets/nb-c-cart-content.liquid into
  // <script data-nb-cart-strings>. Each call falls back to the English text.
  function str(el, name, fallback) {
    var root = el && (el.closest('[data-nb-cart-root]') || el);
    if (root && !root.__nbStrings) {
      var tag = root.querySelector('script[data-nb-cart-strings]');
      try { root.__nbStrings = tag ? JSON.parse(tag.textContent) : {}; } catch (e) { root.__nbStrings = {}; }
    }
    var v = root && root.__nbStrings ? root.__nbStrings[name] : null;
    return v || fallback;
  }
  function esc(s) {
    return String(s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; });
  }
  // Locale-aware URLs: on /ms/ or /zh/ the cart, product JSON and links stay in that language.
  function url(path) {
    var r = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return r.replace(/\/?$/, '/') + path.replace(/^\//, '');
  }

  function roots() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-nb-cart-root]'));
  }

  function moneyFormat(el) {
    var format = el && el.dataset.moneyFormat;
    // No currency symbol is guessed. `shop.money_format` is always passed in from Liquid
    // (see the section's `data-money-format` attribute); if it is somehow missing, show
    // the bare amount rather than a wrong or assumed currency mark. Blank beats guessed.
    return format || '{{amount_no_decimals}}';
  }

  function money(cents, el) {
    // Whole units, matching Liquid's money_without_trailing_zeros, wrapped in the shop's
    // own money_format template so the currency symbol and its position are always correct.
    var value = Math.round(cents / 100);
    var withComma = value.toLocaleString('en-US');
    return moneyFormat(el)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, withComma)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, withComma)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, value)
      .replace(/\{\{\s*amount\s*\}\}/, value);
  }

  function eligibleProductIds(el) {
    var raw = (el && el.dataset.eligibleProducts) || '';
    return raw.split(',').filter(Boolean).map(Number);
  }

  function warrantyProductId(el) {
    var raw = el && el.dataset.warrantyProductId;
    return raw ? Number(raw) : null;
  }

  function renderEmpty(el) {
    // Empty state, both the drawer and the /cart page (same markup, shared snippet):
    // the sleeping-bottle illustration frozen on Paper board UGM-0 (file
    // 01M2M4YEG7NTF94XKNWGAFCQTZ), static (no motion pass), "Your cart is empty." and a
    // "Shop Clean Max" button. Board carried no literal copy beyond this line, so the
    // button text/target is the job brief's own fallback.
    var img = el.getAttribute('data-nb-cart-illustration');
    var wrap = document.createElement('div');
    wrap.className = 'nb-cart-page__empty';
    if (img) {
      var imgEl = document.createElement('img');
      imgEl.loading = 'lazy'; // the drawer is hidden on load, so this waits until a shopper opens it
      imgEl.src = img;
      imgEl.alt = '';
      imgEl.width = 160;
      imgEl.height = 155;
      wrap.appendChild(imgEl);
    }
    var p = document.createElement('p');
    p.className = 'text-body font-sans text-ink font-medium';
    p.textContent = str(el, 'empty', 'Your cart is empty.');
    wrap.appendChild(p);
    var root = el.closest('[data-nb-cart-root]');
    var tpl = root && root.querySelector('[data-nb-cart-empty-cta-template]');
    if (tpl) wrap.appendChild(tpl.content.cloneNode(true));
    el.innerHTML = '';
    el.appendChild(wrap);
  }

  // Cart contents line, per handle. Card copy already used on the site (see
  // templates/collection.nb-refills.json splitpack blocks and the Clean Max bundle
  // compare table, templates/product.nb-clean-max.json): /cart.js does not expose bundle
  // components, so this reuses that same wording rather than typing new copy. Only the
  // bonus/refill contents are counted, matching how those cards count boxes (the base
  // machine's own tablets/filter are never listed here).
  var BUNDLE_CONTENTS = {
    // Clean365 / Clean730: exact strings from the Clean Max buy box option cards,
    // sections/nb-cmax-buy.liquid checklist_12 / checklist_24 (all-boxes count).
    // [string name in the cart strings block, English fallback]
    'clean-max-365-bundle': ['contents_365', '6 boxes of washing tablets, 2 boxes of HEPA H13 filters, 1 box of descaler'],
    'clean-max-730-bundle': ['contents_730', '12 boxes of washing tablets, 4 boxes of HEPA H13 filters, 2 boxes of descaler'],
    '3-wash-hepa-descale-pack-clean-max': ['contents_pack', '3 boxes of washing tablets, 1 box of HEPA H13 filters, 1 box of descaler'],
    '3-wash-hepa-descale-pack-clean': ['contents_pack', '3 boxes of washing tablets, 1 box of HEPA H13 filters, 1 box of descaler']
  };

  // Per-handle product JSON cache (variants carry inventory_quantity/inventory_policy,
  // which /cart.js line items do not expose), so a repaint never re-fetches a handle
  // it already has.
  var productCache = {};
  function fetchProduct(handle) {
    if (productCache[handle]) return productCache[handle];
    productCache[handle] = fetch(url('/products/' + handle + '.js'), { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
    return productCache[handle];
  }

  // Card-links job, 2026-09-30: a cart line's image/title area navigates to the real
  // product page with that variant/option preselected, same destinations as the site's
  // bundle/product cards (specs/GO-LIVE-FIX-BRIEF.md). Bundle handles route to the Clean
  // Max page with the matching option; the accessory pack routes to the washing tablets
  // refill picker; everything else is its own product page with ?variant=.
  function lineHref(line) {
    var handle = line.handle || '';
    if (handle === 'clean-max-365-bundle') return url('/products/nuviebaby-clean-max?option=m12');
    if (handle === 'clean-max-730-bundle') return url('/products/nuviebaby-clean-max?option=m24');
    if (handle === '3-wash-hepa-descale-pack-clean-max' || handle === '3-wash-hepa-descale-pack-clean') {
      return url('/products/super-clean-washing-tablets?option=pack');
    }
    if (handle === 'nuviebaby-clean-max') return url('/products/nuviebaby-clean-max?option=none');
    if (!handle) return '';
    return url('/products/' + handle + (line.variant_id ? '?variant=' + line.variant_id : ''));
  }

  function lineRow(line, root) {
    var img = line.image ? 'background-image:url(' + line.image.replace(/\.(jpg|jpeg|png|webp|gif)(\?|$)/i, '_200x200.$1$2') + ')' : '';
    var props = line.properties && line.properties._details ? line.properties._details : line.variant_title && line.variant_title !== 'Default Title' ? line.variant_title : '';
    var contentsKey = BUNDLE_CONTENTS[line.handle] ? BUNDLE_CONTENTS[line.handle][0] : '';
    var contents = contentsKey ? esc(str(root, contentsKey, BUNDLE_CONTENTS[line.handle][1])) : '';
    var href = lineHref(line);
    var row = document.createElement('div');
    row.className = 'nb-cart-line';
    row.setAttribute('data-nb-cart-line', line.key);
    if (href) {
      row.setAttribute('data-nb-card-href', href);
      row.style.cursor = 'pointer';
    }
    row.innerHTML =
      '<div class="nb-cart-line__thumb" style="' + img + '"></div>' +
      '<div class="flex flex-col grow gap-[6px]">' +
        '<div class="flex justify-between gap-[12px]">' +
          '<span class="text-body leading-[21px] font-sans font-medium text-ink">' + line.product_title + '</span>' +
          '<span class="inline-flex items-baseline gap-[8px] shrink-0">' +
            '<span class="text-body leading-[21px] font-sans font-medium text-ink">' + money(line.final_line_price, root) + '</span>' +
            '<span class="text-meta leading-[18px] font-sans text-mute line-through hidden" data-nb-cart-was></span>' +
          '</span>' +
        '</div>' +
        '<div class="hidden" data-nb-cart-save><span class="nb-c-badge bg-sky text-cobalt inline-flex items-center rounded-[999px] text-[13px] font-medium px-[12px] py-[6px]"></span></div>' +
        (props ? '<span class="text-meta leading-[18px] font-sans text-mute">' + props + '</span>' : '') +
        (contents ? '<span class="text-meta leading-[18px] font-sans text-mute">' + contents + '</span>' : '') +
        '<div class="flex items-center justify-between pt-[6px]">' +
          '<div class="nb-cart-line__qty">' +
            '<button type="button" data-nb-cart-qty-minus class="text-body font-sans text-mute" aria-label="' + esc(str(root, 'decrease_quantity', 'Decrease quantity')) + '">&minus;</button>' +
            '<span class="w-[28px] text-center text-caption font-sans font-medium text-ink" data-nb-cart-qty-value>' + line.quantity + '</span>' +
            '<button type="button" data-nb-cart-qty-plus class="text-body font-sans text-ink" aria-label="' + esc(str(root, 'increase_quantity', 'Increase quantity')) + '">+</button>' +
          '</div>' +
          '<button type="button" data-nb-cart-remove class="text-meta font-sans text-mute" style="text-decoration:underline;">' + esc(str(root, 'remove', 'Remove')) + '</button>' +
        '</div>' +
      '</div>';
    return row;
  }

  // Bundle lines show the struck full price and the saving, the same way the product
  // cards do (nb-c-price, nb-c-badge). /cart.js carries no compare-at price, so it comes
  // from the variant on /products/<handle>.js. Nothing shows when there is no saving.
  function paintBundleSavings(items, linesEl, root) {
    items.forEach(function (line) {
      if (!BUNDLE_CONTENTS[line.handle]) return;
      fetchProduct(line.handle).then(function (product) {
        if (!product || !Array.isArray(product.variants)) return;
        var variant = product.variants.filter(function (v) { return v.id === line.variant_id; })[0];
        var compare = variant && variant.compare_at_price ? variant.compare_at_price * line.quantity : 0;
        if (!(compare > line.final_line_price)) return;
        var row = linesEl.querySelector('[data-nb-cart-line="' + line.key + '"]');
        var was = row && row.querySelector('[data-nb-cart-was]');
        var save = row && row.querySelector('[data-nb-cart-save]');
        if (was) { was.textContent = money(compare, root); was.classList.remove('hidden'); }
        if (save) { save.firstChild.textContent = 'Save ' + money(compare - line.final_line_price, root); save.classList.remove('hidden'); }
      });
    });
  }

  function updateCartCount(count) {
    document.querySelectorAll('[data-nb-cart-count]').forEach(function (el) {
      el.textContent = count;
      el.classList.toggle('hidden', count === 0);
    });
  }

  function paintOne(cart, el) {
    if (!cart || !Array.isArray(cart.items)) return; // an error reply (rate limit) has no items
    var itemCountEl = el.querySelector('[data-nb-cart-item-count]');
    var linesEl = el.querySelector('[data-nb-cart-lines]');
    var offerEl = el.querySelector('[data-nb-cart-offer]');
    var subtotalEl = el.querySelector('[data-nb-cart-subtotal]');
    var totalEl = el.querySelector('[data-nb-cart-total]');
    var footerEl = el.querySelector('[data-nb-cart-footer]');
    var empty = cart.item_count === 0;

    // Empty cart (page and drawer, per PM ruling): hide Subtotal/Delivery/Total,
    // Checkout and the trust line entirely, not just zero them out.
    if (footerEl) footerEl.hidden = empty;

    if (itemCountEl) itemCountEl.textContent = str(el, cart.item_count === 1 ? 'item_one' : 'item_other', cart.item_count === 1 ? '{count} item' : '{count} items').replace('{count}', cart.item_count);
    if (subtotalEl) subtotalEl.textContent = money(cart.total_price, el);
    if (totalEl) totalEl.textContent = money(cart.total_price, el);

    if (linesEl) {
      linesEl.innerHTML = '';
      if (cart.item_count === 0) {
        renderEmpty(linesEl);
      } else {
        cart.items.forEach(function (line) {
          linesEl.appendChild(lineRow(line, el));
        });
        paintBundleSavings(cart.items, linesEl, el);
      }
    }

    // Rule 2 (upsell-cross-sell-strategy.md, priority #2): a machine or bundle in the
    // cart with no 2nd year warranty. Eligibility and the warranty product are both
    // decided from explicit product ids (the `eligible_products` and `warranty_product`
    // settings), never a title match. Rules 1, 3, 4 of that doc are not built here; see
    // the section comment and this component's handback.
    if (offerEl) {
      var offerButton = offerEl.querySelector('[data-nb-cart-offer-add]');
      var eligibleIds = eligibleProductIds(el);
      var warrantyId = warrantyProductId(el);
      var hasWarranty = warrantyId != null && cart.items.some(function (line) {
        return line.product_id === warrantyId;
      });
      var hasEligibleProduct = eligibleIds.length > 0 && cart.items.some(function (line) {
        return eligibleIds.indexOf(line.product_id) !== -1;
      });
      var show = offerButton && hasEligibleProduct && !hasWarranty;
      offerEl.hidden = !show;
    }
  }

  function paint(cart) {
    updateCartCount(cart && Array.isArray(cart.items) ? cart.item_count : 0);
    roots().forEach(function (el) {
      paintOne(cart, el);
    });
  }

  function fetchCart() {
    return fetch(url('/cart.js'), { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
  }

  function refresh() {
    return fetchCart().then(paint);
  }

  function refreshAndOpen() {
    return refresh().then(function () {
      var trigger = document.querySelector('[data-nb-cart-auto-open]');
      if (trigger) trigger.click();
    });
  }

  function changeItem(key, quantity) {
    return fetch(url('/cart/change.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    }).then(function (r) { return r.json(); }).then(paint);
  }

  function addVariant(variantId) {
    return fetch(url('/cart/add.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    }).then(function (r) {
      if (!r.ok) return r.json().then(function (err) { throw err; });
      return r.json();
    }).then(function () {
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    }).catch(function (err) {
      // eslint-disable-next-line no-console
      console.error('nb-shell-cart-drawer: add failed', err);
    });
  }

  function bindLineEvents(el) {
    if (el.hasAttribute('data-nb-cart-lines-bound')) return;
    el.setAttribute('data-nb-cart-lines-bound', '');
    el.addEventListener('click', function (e) {
      var row = e.target.closest('[data-nb-cart-line]');
      if (!row) return;
      var key = row.getAttribute('data-nb-cart-line');
      var qtyEl = row.querySelector('[data-nb-cart-qty-value]');
      var qty = qtyEl ? parseInt(qtyEl.textContent, 10) : 1;
      if (e.target.closest('[data-nb-cart-qty-minus]')) {
        changeItem(key, Math.max(0, qty - 1));
      } else if (e.target.closest('[data-nb-cart-qty-plus]')) {
        changeItem(key, qty + 1);
      } else if (e.target.closest('[data-nb-cart-remove]')) {
        changeItem(key, 0);
      }
    });
  }

  function bindOfferEvents(el) {
    if (el.hasAttribute('data-nb-cart-offer-bound')) return;
    el.setAttribute('data-nb-cart-offer-bound', '');
    el.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-nb-cart-offer-add]');
      if (!btn) return;
      var variantId = btn.getAttribute('data-variant-id');
      if (variantId) addVariant(variantId);
    });
  }

  function init() {
    var found = false;
    roots().forEach(function (el) {
      if (el.hasAttribute('data-nb-cart-drawer-init')) return;
      el.setAttribute('data-nb-cart-drawer-init', '');
      found = true;
      var linesEl = el.querySelector('[data-nb-cart-lines]');
      var offerEl = el.querySelector('[data-nb-cart-offer]');
      if (linesEl) bindLineEvents(linesEl);
      if (offerEl) bindOfferEvents(offerEl);
    });
    if (found) refresh();
  }

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('cart:refresh', refreshAndOpen);
})();
